import React, { useState } from 'react';
import { TID } from '@/constants/testIds';
import { storage } from '@/lib/storage';
import { sounds } from '@/audio/sounds';
import { ensureGuest, loadGuest } from '@/lib/guestIdentity';
import GuestNameModal from '@/components/GuestNameModal';
import InstallPrompt from '@/components/InstallPrompt';
import { isWakeLockSupported } from '@/hooks/useScreenWakeLock';
import { PremiumBackButton, PremiumHomeButton } from '@/components/PremiumNav';
import { RankedBackdrop } from '@/components/RankedChrome';

/**
 * Kickard PHASE 5.0A PASS 3 — Settings information architecture.
 *
 * Existing controls are grouped into clearly-labelled sections:
 *   AUDIO      · Sound effects
 *   PLAYER     · Display name
 *   APP        · Keep screen awake · Install Kickard
 *   HELP       · Replay tutorial
 *   ADVANCED   · Developer mode
 *   DANGER     · Reset stats & settings (visually + spatially isolated)
 *
 * ZERO functional change — every toggle / edit / reset / tutorial
 * primer / dev-mode gating behaves exactly as before.
 */
export default function SettingsScreen({ onClose }) {
  const [settings, setSettings] = useState(storage.getSettings());
  const [guest, setGuest] = useState(() => ensureGuest());
  const [editingName, setEditingName] = useState(false);

  const toggleSound = () => {
    const next = !settings.soundEnabled;
    setSettings((s) => ({ ...s, soundEnabled: next }));
    storage.setSettings({ soundEnabled: next });
    sounds.setEnabled(next);
  };

  const toggleWakeLock = () => {
    const next = !settings.keepScreenAwake;
    setSettings((s) => ({ ...s, keepScreenAwake: next }));
    storage.setSettings({ keepScreenAwake: next });
  };
  const wakeLockSupported = isWakeLockSupported();

  const replayTutorial = () => {
    storage.setSettings({ tutorialPrimerSeen: false, tutorialSkipped: false, tutorialFirstBotMatchCompleted: false });
    onClose();
  };

  const resetStats = () => {
    if (window.confirm('Reset all stats?')) {
      storage.reset();
      sounds.setEnabled(storage.getSettings().soundEnabled);
      setSettings(storage.getSettings());
    }
  };

  return (
    <RankedBackdrop testid={TID.settingsScreen}>
      <div className="kset kst__inner">
        <header className="kst__header">
          <PremiumBackButton testid={TID.settingsClose} onClick={onClose} />
          <div className="kst__titles">
            <h1 className="kst__title" data-testid="settings-title">Settings</h1>
            <div className="kst__subtitle" data-testid="settings-subtitle">GAME PREFERENCES</div>
          </div>
          <PremiumHomeButton testid="settings-home-btn" />
        </header>

        <Group label="Audio">
          <Row label="Sound effects" desc="Web Audio synthesized SFX">
            <Segmented label="Sound effects" on={!!settings.soundEnabled} onToggle={toggleSound} testid={TID.settingsSoundToggle} />
          </Row>
        </Group>

        <Group label="Player">
          <Row label="Display name" desc={(guest && guest.displayName) || 'No name set'} valueDesc>
            <button
              type="button"
              data-testid="settings-edit-name-btn"
              className="kset-btn"
              onClick={() => setEditingName(true)}
              aria-label="Edit name"
            >
              EDIT
            </button>
          </Row>
        </Group>

        <Group label="App">
          <Row
            label="Keep screen awake"
            desc={wakeLockSupported
              ? 'Stops phone auto-lock during a match'
              : 'Not supported on this browser'}
          >
            {wakeLockSupported ? (
              <Segmented label="Keep screen awake" on={!!settings.keepScreenAwake} onToggle={toggleWakeLock} testid="settings-wakelock-toggle" />
            ) : (
              <span data-testid="settings-wakelock-unsupported" className="kset-chip">UNSUPPORTED</span>
            )}
          </Row>
          <Row label="Install Kickard" desc="Add to your phone's home screen">
            <InstallPrompt
              label="Install"
              fallback={(state) => (state === 'installed'
                ? <span className="kset-chip kset-chip--gold" data-testid="settings-install-status">Already installed</span>
                : <span className="kset-chip" data-testid="settings-install-status">Not available</span>)}
            />
          </Row>
        </Group>

        <Group label="Help">
          <Row label="Replay tutorial" desc="Show the intro again next time you play">
            <button type="button" className="kset-btn" data-testid="settings-replay-tutorial-btn" onClick={replayTutorial}>Replay</button>
          </Row>
        </Group>

        <Group label="Advanced" className="kset-group--advanced">
          <Row label="Developer mode" desc={settings.devEnabled ? 'Enabled — tap 5x logo to disable' : 'Tap the logo 5 times on home to enable'}>
            <span className={`kset-chip${settings.devEnabled ? ' kset-chip--gold' : ''}`} data-testid="settings-dev-state">
              {settings.devEnabled ? 'ON' : 'OFF'}
            </span>
          </Row>
        </Group>

        {/* DANGER ZONE — visually and spatially isolated from ordinary settings. */}
        <div className="kset-danger" data-testid="settings-danger-zone">
          <div className="kset-danger-panel">
            <div className="kset-danger-title" style={{ marginBottom: 11 }}>Danger Zone</div>
            <button
              type="button"
              data-testid={TID.settingsResetStats}
              className="kset-danger-btn"
              onClick={resetStats}
            >
              Reset stats &amp; settings
            </button>
          </div>
        </div>
      </div>

      {editingName && (
        <GuestNameModal
          mode="edit"
          initial={(guest && guest.displayName) || ''}
          onSaved={(g) => {
            setGuest(g || loadGuest());
            setEditingName(false);
          }}
          onCancel={() => setEditingName(false)}
        />
      )}
    </RankedBackdrop>
  );
}

function Group({ label, className = '', children }) {
  return (
    <section className={`kset-group ${className}`} data-testid={`settings-group-${label.toLowerCase()}`}>
      <h2 className="kset-label">{label}</h2>
      <div className="kset-panel">{children}</div>
    </section>
  );
}

function Row({ label, desc, valueDesc = false, children }) {
  return (
    <div className="kset-row">
      <div className="kset-copy">
        <div className="kset-name">{label}</div>
        <div className={`kset-desc${valueDesc ? ' kset-desc--value' : ''}`}>{desc}</div>
      </div>
      <div className="kset-ctrl">{children}</div>
    </div>
  );
}

// ON | OFF segmented control; tapping the already-selected side is a no-op.
function Segmented({ label, on, onToggle, testid }) {
  return (
    <div className="kset-seg" role="group" aria-label={label} data-testid={testid} data-state={on ? 'on' : 'off'}>
      <button type="button" aria-pressed={on} data-testid={`${testid}-on`} onClick={() => !on && onToggle()}>ON</button>
      <button type="button" aria-pressed={!on} data-testid={`${testid}-off`} onClick={() => on && onToggle()}>OFF</button>
    </div>
  );
}
